// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, googleProvider } from "../services/firebaseConfig";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail
} from "firebase/auth";
import supabase from "../services/SupabaseService";
import { useEpocasContext } from "./EpocasContext";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [logado, setLogado] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [funcao, setFuncao] = useState(null); // Função original na tabela Emails
  const [epocasPermitidas, setEpocasPermitidas] = useState([]);
  const [mostrarToast, setMostrarToast] = useState(false);
  const { epocaSelecionada } = useEpocasContext();
  const funcoesOriginais = String(funcao || '').split(',').map(f => f.trim()).filter(Boolean);
  const adminEfetivo = logado && funcoesOriginais.includes('Admin');
  const podeAcederEpoca = (epoca) => Boolean(
    logado && epoca && (adminEfetivo || epocasPermitidas.includes(String(epoca).trim()))
  );
  const funcoesAtivas = logado ? funcoesOriginais.filter(f =>
    f === 'Admin' || !['Jogador', 'EquipaTecnica'].includes(f) || podeAcederEpoca(epocaSelecionada)
  ) : [];
  const funcaoEfetiva = funcoesAtivas.join(',') || 'User';
  const isJogador = funcoesAtivas.includes('Jogador');
  const isEquipaTecnica = funcoesAtivas.includes('EquipaTecnica');
  const podeVerEstatisticas = adminEfetivo || isJogador || isEquipaTecnica;

  useEffect(() => {
    let ativo = true;
    let versao = 0;
    const unsubscribe = onAuthStateChanged(auth, async (usuario) => {
      const pedido = ++versao;
      setLoading(true);
      setLogado(!!usuario);
      setUser(usuario || null);
      setFuncao(null);
      setIsAdmin(false);
      setEpocasPermitidas([]);

      if (usuario?.email) {

        // Buscar no Supabase a função do usuário logado
        const { data, error } = await supabase
          .from("Emails")
          .select("Funcao, Epocas")
          .ilike("Email", usuario.email.trim())
          .maybeSingle();
          
        if (!ativo || pedido !== versao) return;
        if (!error && data) {
          setFuncao(data.Funcao);
          setEpocasPermitidas(String(data.Epocas || '').split(',').map(e => e.trim()).filter(Boolean));
          setIsAdmin(String(data.Funcao || '').split(',').map(f => f.trim()).includes('Admin'));
        }
      }
      if (ativo && pedido === versao) setLoading(false);
    });

    return () => { ativo = false; versao++; unsubscribe(); };
  }, []);

  const tentarLogin = async (email, senha) => {
    return await signInWithEmailAndPassword(auth, email, senha);
  };

  const registar = async (email, senha, nome) => {
    const credenciais = await createUserWithEmailAndPassword(auth, email, senha);
    await updateProfile(credenciais.user, { displayName: nome });
    return credenciais.user;
  };

  const loginGoogle = async () => {
    return await signInWithPopup(auth, googleProvider);
  };

  const logout = async () => {
    return await signOut(auth);
  };

  const resetSenha = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert("Email de redefinição de senha enviado!");
    } catch (error) {
      console.error(error);
      alert("Erro ao enviar email: " + error.message);
    }
  };

  return (
    <AuthContext.Provider value={{
      logado,
      user,
      loading,
      isAdmin,
      funcao,
      funcaoEfetiva,
      epocasPermitidas,
      mostrarToast,
      setMostrarToast,
      podeAcederEpoca,
      isJogador,
      isEquipaTecnica,
      podeVerEstatisticas,
      tentarLogin,
      registar,
      loginGoogle,
      logout,
      resetSenha
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}
